/**
 * The aircraft profile document — `aircraft.profile` version 1.
 *
 * This is the pilot's own airframe (tier 2 in `aircraftPresets.ts`): the empty
 * mass from ITS weighing report, the usable fuel of ITS tanks, the burn the club
 * actually plans with. A type preset only ever SEEDS it; every seeded number is
 * an estimate until the pilot ticks it off against the POH or the weighing
 * report, which is what {@link AircraftProfile.confirmed} records.
 *
 * The shape is a contract with `server/api` (`aircraft.rs` validates the same
 * document) and with the plan feed in the web app, so it lives here rather than
 * in the app: browser, Node and — later — React Native read the same profile.
 *
 * Everything is nullable. **Absent or null means unknown**, and an unknown value
 * is never guessed: a plan cell it would have filled simply stays empty.
 */
export const UNIT_MASS = ['kg', 'lbs.'];
export const UNIT_MOMENT = ['kgm', 'x 100', 'x 1000'];
/* ---------------- limits (the API validates the same ones) ---------------- */
export const MAX_TAS_KT = 400;
export const MAX_LPH = 500;
export const MAX_MTOW_KG = 20000;
export const MAX_CRUISE_ROWS = 12;
export const MAX_ALT_FT = 60000;
export const MAX_POWER_PCT = 100;
export const MAX_RATE_FPM = 10000;
export const MAX_FUEL_L = 20000;
export const MAX_RESERVE_MIN = 600;
export const MAX_SEATS = 20;
export const MAX_ARM_M = 100;
export const MAX_MOMENT = 1e7;
export const MAX_NOTES = 2000;
export const MAX_CONFIRMED = 64;
/** A `confirmed` entry is a dotted field path; the API bounds it at 40 characters. */
export const MAX_CONFIRMED_PATH = 40;
/** Avgas density used for the unusable-fuel mass row (kg per litre). */
export const AVGAS_KG_PER_L = 0.72;
/** The reserve a new profile starts with, minutes. Shown as an estimate, never as a rule. */
export const DEFAULT_RESERVE_MIN = 45;
/* ---------------- parsing ---------------- */
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/**
 * A non-negative number within `max`, or null. Anything else — a string, NaN, a
 * negative mass, a TAS of 40 000 kt — is *unknown*, not clamped: a clamped value
 * looks like a measurement and would be planned with.
 */
function n(v, max) {
    const x = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    if (!Number.isFinite(x) || x < 0 || x > max)
        return null;
    return x;
}
const str = (v, max) => {
    if (typeof v !== 'string')
        return null;
    const s = v.trim();
    return s ? s.slice(0, max) : null;
};
const allNull = (o) => Object.values(o).every(v => v === null);
function parseCruise(v) {
    if (!isObj(v))
        return null;
    const c = {
        tas_kt: n(v['tas_kt'], MAX_TAS_KT),
        fuel_lph: n(v['fuel_lph'], MAX_LPH),
        power_pct: n(v['power_pct'], MAX_POWER_PCT),
        alt_ft: n(v['alt_ft'], MAX_ALT_FT),
    };
    return allNull(c) ? null : c;
}
function parseCruiseTable(v) {
    if (!Array.isArray(v))
        return null;
    const rows = [];
    for (const raw of v) {
        if (rows.length >= MAX_CRUISE_ROWS)
            break;
        if (!isObj(raw))
            continue;
        const row = {
            power_pct: n(raw['power_pct'], MAX_POWER_PCT),
            alt_ft: n(raw['alt_ft'], MAX_ALT_FT),
            tas_kt: n(raw['tas_kt'], MAX_TAS_KT),
            lph: n(raw['lph'], MAX_LPH),
        };
        if (!allNull(row))
            rows.push(row);
    }
    return rows.length ? rows : null;
}
function parseClimb(v) {
    if (!isObj(v))
        return null;
    const c = { rate_fpm: n(v['rate_fpm'], MAX_RATE_FPM), lph: n(v['lph'], MAX_LPH) };
    return allNull(c) ? null : c;
}
function parseFuel(v) {
    if (!isObj(v))
        return null;
    const f = {
        usable_l: n(v['usable_l'], MAX_FUEL_L),
        unusable_l: n(v['unusable_l'], MAX_FUEL_L),
        taxi_l: n(v['taxi_l'], MAX_FUEL_L),
        reserve_min: n(v['reserve_min'], MAX_RESERVE_MIN),
    };
    return allNull(f) ? null : f;
}
function parseMass(v) {
    if (!isObj(v))
        return null;
    const um = v['unit_mass'];
    const uo = v['unit_moment'];
    const m = {
        empty_kg: n(v['empty_kg'], MAX_MTOW_KG),
        empty_moment: n(v['empty_moment'], MAX_MOMENT),
        empty_arm_m: n(v['empty_arm_m'], MAX_ARM_M),
        mtow_kg: n(v['mtow_kg'], MAX_MTOW_KG),
        unit_mass: UNIT_MASS.includes(um) ? um : null,
        unit_moment: UNIT_MOMENT.includes(uo) ? uo : null,
    };
    return allNull(m) ? null : m;
}
function parseConfirmed(v) {
    if (!Array.isArray(v))
        return [];
    const out = [];
    for (const x of v) {
        if (typeof x !== 'string')
            continue;
        const s = x.trim().slice(0, MAX_CONFIRMED_PATH);
        if (s && !out.includes(s))
            out.push(s);
        if (out.length >= MAX_CONFIRMED)
            break;
    }
    return out.sort();
}
/** The profile of an aircraft nobody has filled in yet. */
export function emptyProfile() {
    return {
        v: 1,
        preset_id: null,
        icao: null,
        type_label: null,
        cruise: null,
        cruise_table: null,
        climb: null,
        fuel: null,
        mass: null,
        seats: null,
        notes: null,
        confirmed: [],
    };
}
/**
 * Read whatever the API or an older client stored. A document we cannot
 * understand becomes an empty profile rather than an exception — a broken
 * profile must never keep a pilot out of their aircraft list.
 */
export function parseAircraftProfile(raw) {
    if (!isObj(raw))
        return emptyProfile();
    return {
        v: 1,
        preset_id: str(raw['preset_id'], 64),
        icao: str(raw['icao'], 8)?.toUpperCase() ?? null,
        type_label: str(raw['type_label'], 80),
        cruise: parseCruise(raw['cruise']),
        cruise_table: parseCruiseTable(raw['cruise_table']),
        climb: parseClimb(raw['climb']),
        fuel: parseFuel(raw['fuel']),
        mass: parseMass(raw['mass']),
        seats: n(raw['seats'], MAX_SEATS),
        notes: str(raw['notes'], MAX_NOTES),
        confirmed: parseConfirmed(raw['confirmed']),
    };
}
/** Is this profile empty enough that it can fill nothing at all? */
export const isBlankProfile = (p) => !p.cruise && !p.cruise_table && !p.climb && !p.fuel && !p.mass && !p.icao && !p.type_label;
/* ---------------- reading a profile ---------------- */
/**
 * The cruise setting a plan is flown at: what the pilot entered, else the
 * highest-power row of the table (the row a club aircraft is usually flown at,
 * the same choice `presetDefaults` makes).
 */
export function cruiseSetting(p) {
    if (!p)
        return null;
    if (p.cruise && (p.cruise.tas_kt !== null || p.cruise.fuel_lph !== null))
        return p.cruise;
    const row = bestCruiseRow(p.cruise_table ?? null);
    if (!row)
        return p.cruise;
    return { tas_kt: row.tas_kt, fuel_lph: row.lph, power_pct: row.power_pct, alt_ft: row.alt_ft };
}
/** The row with the highest power setting that has a TAS; the first row otherwise. */
export function bestCruiseRow(rows) {
    if (!rows || rows.length === 0)
        return null;
    let best;
    for (const r of rows) {
        if (r.tas_kt == null)
            continue;
        if (!best || (r.power_pct ?? 0) > (best.power_pct ?? 0))
            best = r;
    }
    return best ?? rows[0] ?? null;
}
/** Was this field taken from the pilot's own POH / weighing report? */
export const isConfirmed = (p, path) => !!p && p.confirmed.includes(path);
/* ---------------- seeding from a type preset ---------------- */
/**
 * Seed a profile from a type preset. Everything it writes is tier 1 — a
 * publication's figure for the TYPE — and therefore lands with an empty
 * `confirmed` list.
 *
 * `typical_empty_kg` is deliberately included, because a pilot staring at an
 * empty W&B block has no order of magnitude at all; it is the one number the
 * preset table itself calls a sanity anchor, so the UI must show it as an
 * estimate until the weighing report confirms it.
 */
export function profileFromPreset(p) {
    const rows = (p.cruise ?? []).slice(0, MAX_CRUISE_ROWS).map((r) => ({
        power_pct: r.power_pct,
        alt_ft: r.alt_ft,
        tas_kt: r.tas_kt,
        lph: r.lph,
    }));
    const best = bestCruiseRow(rows);
    return {
        v: 1,
        preset_id: p.id,
        icao: p.icao,
        type_label: (p.manufacturer + ' ' + p.model).trim(),
        cruise: {
            tas_kt: best?.tas_kt ?? null,
            fuel_lph: p.fuel.flow_lph_default ?? best?.lph ?? null,
            power_pct: best?.power_pct ?? null,
            alt_ft: best?.alt_ft ?? null,
        },
        cruise_table: rows.length ? rows : null,
        climb: { rate_fpm: p.climb_fpm_sl ?? null, lph: null },
        fuel: {
            usable_l: p.fuel.usable_l,
            unusable_l: null,
            taxi_l: null,
            // not a preset figure: the app's planning default. The legal minimum
            // depends on the operation (NCO.OP.125), so the pilot confirms it.
            reserve_min: DEFAULT_RESERVE_MIN,
        },
        mass: {
            empty_kg: p.typical_empty_kg,
            empty_moment: null,
            empty_arm_m: null,
            mtow_kg: p.mtow_kg,
            unit_mass: 'kg',
            unit_moment: 'kgm',
        },
        seats: p.seats,
        notes: null,
        confirmed: [],
    };
}
//# sourceMappingURL=aircraftProfile.js.map